import React from 'react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { useTriFormat, type TriFormatField } from '@/hooks/useTriFormat'

interface AssetWithPrice {
  id: string
  symbol: string
  name: string
  currentPrice: number
  assetClass: 'CRYPTO' | 'TRADFI'
}

interface Position {
  assetId: string
  quantity: number
  value: number
}

interface PositionChangeInputProps {
  asset: AssetWithPrice
  currentPosition: Position
  onChange: (delta: { quantityDelta: number; newQuantity: number }) => void
  disabled?: boolean
  initialValues?: {
    qty?: string
    pct?: string
    dollar?: string
  }
}

/**
 * PositionChangeInput - Tri-format input component for portfolio adjustments
 * 
 * Allows users to specify position changes via three interchangeable formats:
 * - Quantity: Absolute number of units
 * - Percentage: Percentage change from current position
 * - Dollar: Dollar value of the new position
 * 
 * When user enters one value, the others auto-calculate using T-1 reference price.
 * 
 * @example
 * ```tsx
 * <PositionChangeInput
 *   asset={{ id: '1', symbol: 'BTC', name: 'Bitcoin', currentPrice: 65000, assetClass: 'CRYPTO' }}
 *   currentPosition={{ assetId: '1', quantity: 1.5, value: 97500 }}
 *   onChange={(delta) => console.log('New quantity:', delta.newQuantity)}
 * />
 * ```
 */
export function PositionChangeInput({
  asset,
  currentPosition,
  onChange,
  disabled = false,
  initialValues,
}: PositionChangeInputProps) {
  const { values, activeField, updateField } = useTriFormat(
    asset.currentPrice,
    currentPosition.quantity
  )

  // Initialize with provided values if in edit mode
  React.useEffect(() => {
    if (initialValues) {
      // Set initial values through the hook
      if (initialValues.qty) {
        updateField('qty', initialValues.qty)
      } else if (initialValues.pct) {
        updateField('pct', initialValues.pct)
      } else if (initialValues.dollar) {
        updateField('dollar', initialValues.dollar)
      }
    }
  }, []) // Only run on mount

  // Notify parent when values change
  React.useEffect(() => {
    const newQty = parseFloat(values.qty)
    if (!isNaN(newQty)) {
      const quantityDelta = newQty - currentPosition.quantity
      onChange({
        quantityDelta,
        newQuantity: newQty,
      })
    }
  }, [values.qty, currentPosition.quantity, onChange])

  const handleQtyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateField('qty', e.target.value)
  }

  const handlePctChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateField('pct', e.target.value)
  }

  const handleDollarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateField('dollar', e.target.value)
  }

  // Determine change direction for visual feedback
  const getChangeDirection = () => {
    const newQty = parseFloat(values.qty)
    if (isNaN(newQty) || newQty === currentPosition.quantity) return 'neutral'
    if (newQty > currentPosition.quantity) return 'increase'
    return 'decrease'
  }

  const direction = getChangeDirection()
  const directionColors = {
    increase: 'text-green-600 bg-green-50 border-green-200',
    decrease: 'text-red-600 bg-red-50 border-red-200',
    neutral: 'text-gray-600 bg-gray-50 border-gray-200',
  }

  const activeHighlight = (field: TriFormatField) =>
    activeField === field ? 'ring-2 ring-blue-500 border-blue-500' : ''

  return (
    <div className="space-y-3 p-4 border rounded-lg bg-white">
      {/* Asset Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge
            variant={asset.assetClass === 'CRYPTO' ? 'default' : 'secondary'}
            className="text-xs"
          >
            {asset.assetClass}
          </Badge>
          <span className="font-semibold">{asset.symbol}</span>
          <span className="text-sm text-gray-500">{asset.name}</span>
        </div>
        <div className="text-sm text-gray-500">
          Current: {currentPosition.quantity.toFixed(4)} @ ${asset.currentPrice.toLocaleString()}
        </div>
      </div>

      {/* Tri-format Inputs */}
      <div className="grid grid-cols-3 gap-3">
        {/* Quantity Input */}
        <div className="space-y-1">
          <Label htmlFor={`qty-${asset.id}`} className="text-xs text-gray-600">
            Quantity
          </Label>
          <div className="relative">
            <Input
              id={`qty-${asset.id}`}
              type="number"
              step="0.0001"
              min="0"
              value={values.qty}
              onChange={handleQtyChange}
              disabled={disabled}
              className={`${activeHighlight('qty')} ${directionColors[direction]}`}
              placeholder={currentPosition.quantity.toFixed(4)}
            />
          </div>
          <p className="text-xs text-gray-400">New quantity</p>
        </div>

        {/* Percentage Input */}
        <div className="space-y-1">
          <Label htmlFor={`pct-${asset.id}`} className="text-xs text-gray-600">
            Change %
          </Label>
          <div className="relative">
            <Input
              id={`pct-${asset.id}`}
              type="number"
              step="0.01"
              value={values.pct}
              onChange={handlePctChange}
              disabled={disabled}
              className={`${activeHighlight('pct')} ${directionColors[direction]} pr-6`}
              placeholder="0.00"
            />
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
              %
            </span>
          </div>
          <p className="text-xs text-gray-400">
            {values.pct && !isNaN(parseFloat(values.pct))
              ? parseFloat(values.pct) > 0
                ? `+${values.pct}%`
                : `${values.pct}%`
              : '0.00%'}
          </p>
        </div>

        {/* Dollar Input */}
        <div className="space-y-1">
          <Label htmlFor={`dollar-${asset.id}`} className="text-xs text-gray-600">
            Value ($)
          </Label>
          <div className="relative">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
              $
            </span>
            <Input
              id={`dollar-${asset.id}`}
              type="number"
              step="0.01"
              min="0"
              value={values.dollar}
              onChange={handleDollarChange}
              disabled={disabled}
              className={`${activeHighlight('dollar')} ${directionColors[direction]} pl-6`}
              placeholder={currentPosition.value.toFixed(2)}
            />
          </div>
          <p className="text-xs text-gray-400">
            ${values.dollar && !isNaN(parseFloat(values.dollar))
              ? parseFloat(values.dollar).toLocaleString()
              : currentPosition.value.toLocaleString()}
          </p>
        </div>
      </div>

      {/* Summary */}
      {direction !== 'neutral' && (
        <div
          className={`text-sm px-3 py-2 rounded ${
            direction === 'increase'
              ? 'bg-green-50 text-green-700'
              : 'bg-red-50 text-red-700'
          }`}
        >
          {direction === 'increase' ? '▲' : '▼'} Position will
          {direction === 'increase' ? ' increase' : ' decrease'} from{' '}
          {currentPosition.quantity.toFixed(4)} to{' '}
          {values.qty || currentPosition.quantity.toFixed(4)} units
          {values.dollar && !isNaN(parseFloat(values.dollar)) && (
            <span> (${parseFloat(values.dollar).toLocaleString()} total value)</span>
          )}
        </div>
      )}

      {/* Reference Price Note */}
      <p className="text-xs text-gray-400 italic">
        Calculations use T-1 reference price: ${asset.currentPrice.toLocaleString()}
      </p>
    </div>
  )
}

export default PositionChangeInput
